import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Loader2, RotateCw, SearchX, TriangleAlert } from 'lucide-react';
import { Role } from '@nexa/auth';
import { RoleGate } from '@/auth/role-gate';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { formatMinor } from '@/routes/disruptions/care-desk/care-desk-model';
import { recoveryErrorCode } from '@/lib/recovery/api';
import type { IRecoveryCandidate, IRecoveryView } from '@/lib/recovery/types';
import {
  bestCandidate,
  candidateLine,
  offerable,
  candidateVerdict,
  failureReasonOf,
  reacceptedCandidate,
  failureClause,
  formatDelay,
  providerName,
  type PurchasePhase,
  type RecoveryT,
} from '@/lib/recovery/recovery-model';
import type { RecoveryAction } from '@/lib/recovery/use-recovery';
import { toneClasses } from './airport-tokens';
import { CardAction, SectionTitle } from './pnr-card-ui';
import { cardClock } from './card-time';

// A failed purchase's recovery inside the passenger card (accepted-offer-recovery P1/P7):
//   • RecoveryFlightBody — the «Vuelo nuevo» service card while the purchase is in trouble:
//     the accepted flight (gone), the best replacement, and its own actions.
//   • ReplacementChooser — «elegir reemplazo» (mockup PnrRechazada): every fresh option,
//     offer one, buy without asking (supervisor), leave without flight.
//   • UnconfirmedPanel — «compra sin confirmar» (mockup PnrSinConfirmar): the verification
//     timeline, verify now, and give up with an explicit attestation (supervisor).
// Writes go through `useRecoveryAction` (operator-bff §6.6); the drawer owns the mutation.

export interface RecoveryRun {
  run: (action: RecoveryAction) => void;
  pending: boolean;
  /** The last write's failure, if any, and which write it was. */
  error: Error | null;
  action?: RecoveryAction['kind'] | undefined;
}

const VERDICT_CLASS: Record<ReturnType<typeof candidateVerdict>, string> = {
  equivalent: toneClasses('ok').text,
  offer: toneClasses('warn').text,
  costly: toneClasses('alert').text,
  blocked: 'text-muted-foreground',
};

/** `pnr` as the pure recovery model's translator. */
function useRecoveryT(): RecoveryT {
  const { t } = useTranslation('pnr');
  return (key, opts) => String(t(key, opts ?? {}));
}

/** Departures at the origin, arrivals at the destination — the console's language, 24 h, the
 *  date only when it is not today at that station. */
function useClock(group: DisruptionPnrGroupRow) {
  const { i18n } = useTranslation();
  const destination = group.connection?.finalDestination ?? group.destination;
  const clock = cardClock(i18n.resolvedLanguage ?? i18n.language, group.origin);
  return {
    dep: (iso: string) => clock.moment(iso),
    arr: (iso: string) => clock.moment(iso, destination),
  };
}

function price(c: IRecoveryCandidate): string | undefined {
  return typeof c.priceMinor === 'number' ? formatMinor(c.priceMinor, c.currency ?? 'USD') : undefined;
}

export function RecoveryError({ error, action }: { error: Error | null; action?: RecoveryAction['kind'] | undefined }) {
  const { t } = useTranslation('pnr');
  if (!error) return null;
  const code = recoveryErrorCode(error);
  return (
    <p className="text-[13px] text-destructive" role="alert" data-testid="recovery-error">
      {t(code === 'CONFLICT' && action === 'offer' ? 'recovery.error.CONFLICT_offer' : `recovery.error.${code}`)}
    </p>
  );
}

/** «Comprar sin preguntar…» — supervisor only, behind a confirmation that names the flight. */
export function BuyWithoutAsking({
  candidate,
  group,
  recovery,
  trigger = 'card',
}: {
  candidate: IRecoveryCandidate | undefined;
  group: DisruptionPnrGroupRow;
  recovery: RecoveryRun;
  trigger?: 'card' | 'footer';
}) {
  const { t } = useTranslation('pnr');
  const rt = useRecoveryT();
  const [open, setOpen] = useState(false);
  const clock = useClock(group);
  const label = t('recovery.buy.trigger');
  return (
    <>
      <RoleGate min={Role.OPS_SUPERVISOR} tooltip={t('recovery.supervisorOnly')}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={trigger === 'footer' ? 'h-11 px-4 text-sm' : 'h-9 px-3 text-[13px]'}
          disabled={!candidate || recovery.pending}
          onClick={() => setOpen(true)}
          data-testid="recovery-buy-open"
        >
          {label}
        </Button>
      </RoleGate>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="z-[60] max-w-md" data-testid="recovery-buy-dialog">
          <DialogHeader>
            <DialogTitle>{t('recovery.buy.title', { pnr: group.pnrLabel })}</DialogTitle>
            <DialogDescription>{t('recovery.buy.description')}</DialogDescription>
          </DialogHeader>
          {candidate ? (
            <p className="rounded-lg border border-border px-3 py-2 text-[15px]" data-testid="recovery-buy-flight">
              <b>{candidate.flightNumber}</b> · {clock.dep(candidate.departAt)} → {clock.arr(candidate.arriveAt)}
              {price(candidate) ? ` · ${t('recovery.pricePerPax', { price: price(candidate) })}` : ''}
            </p>
          ) : null}
          {candidate && !candidate.equivalent ? (
            <p className={cn('text-sm', toneClasses('warn').text)}>{candidateLine(candidate, rt)}</p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              {t('recovery.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!candidate || recovery.pending}
              onClick={() => {
                if (!candidate) return;
                recovery.run({ kind: 'buy', optionUrn: candidate.optionUrn });
                setOpen(false);
              }}
              data-testid="recovery-buy-confirm"
            >
              {recovery.pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {t('recovery.buy.confirm', { flight: candidate?.flightNumber ?? '—' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** «Dejar sin vuelo (solo hotel)» — confirmed, because it stops looking for a flight. */
function NoFlight({ group, recovery }: { group: DisruptionPnrGroupRow; recovery: RecoveryRun }) {
  const { t } = useTranslation('pnr');
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-11 border-destructive/50 px-4 text-sm font-semibold text-destructive hover:bg-destructive/10 hover:text-destructive"
        disabled={recovery.pending}
        onClick={() => setOpen(true)}
        data-testid="recovery-no-flight-open"
      >
        {t('recovery.noFlight.trigger')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="z-[60] max-w-md" data-testid="recovery-no-flight-dialog">
          <DialogHeader>
            <DialogTitle>{t('recovery.noFlight.title', { pnr: group.pnrLabel })}</DialogTitle>
            <DialogDescription>{t('recovery.noFlight.description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              {t('recovery.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={recovery.pending}
              onClick={() => {
                recovery.run({ kind: 'noFlight' });
                setOpen(false);
              }}
              data-testid="recovery-no-flight-confirm"
            >
              {t('recovery.noFlight.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ViewLoading() {
  return (
    <div className="flex flex-col gap-2" data-testid="recovery-loading">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function ViewFailed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('pnr');
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" data-testid="recovery-unavailable">
      <span>{t('recovery.viewUnavailable')}</span>
      <button type="button" className="text-primary underline" onClick={onRetry}>
        {t('recovery.retry')}
      </button>
    </div>
  );
}

/** The accepted flight, struck through once it can no longer be bought. `answered`: the
 *  passenger's last answer is about THIS flight (not a re-offer accepted since). */
function AcceptedGone({ group, view, answered = true }: { group: DisruptionPnrGroupRow; view: IRecoveryView; answered?: boolean }) {
  const { t } = useTranslation('pnr');
  const clock = useClock(group);
  const acceptedAt = answered && group.lastOfferResponse?.response === 'ACCEPTED' ? group.lastOfferResponse.at : undefined;
  const a = view.accepted;
  if (!a) return null;
  return (
    <div className="flex flex-wrap justify-between gap-x-3 text-[15px] text-muted-foreground" data-testid="recovery-accepted">
      <span className="line-through">
        {[
          `${a.flightNumber} · ${clock.dep(a.departAt)} → ${clock.arr(a.arriveAt)}`,
          acceptedAt ? t('recovery.acceptedAt', { time: clock.dep(acceptedAt) }) : undefined,
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>
      <span>{t('recovery.gone')}</span>
    </div>
  );
}

function CandidateRow({
  group,
  candidate,
  badge,
}: {
  group: DisruptionPnrGroupRow;
  candidate: IRecoveryCandidate;
  badge: string;
}) {
  const { t } = useTranslation('pnr');
  const rt = useRecoveryT();
  const clock = useClock(group);
  return (
    <div
      className={cn('flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2.5', toneClasses('info').surface)}
      data-testid="recovery-best"
    >
      <span className="text-[15px]">
        <b>{candidate.flightNumber}</b> · {clock.dep(candidate.departAt)} → {clock.arr(candidate.arriveAt)}
        {candidate.arrivalDelayMinutes > 0
          ? ` · ${t('recovery.arrivesLater', { delay: formatDelay(candidate.arrivalDelayMinutes, rt) })}`
          : ''}
      </span>
      <span className={cn('text-[13px]', VERDICT_CLASS[candidateVerdict(candidate)])}>{badge}</span>
    </div>
  );
}

/** The re-offered flight the passenger accepted, whose purchase failed since — and why. */
function ReacceptedRow({
  group,
  candidate,
  why,
}: {
  group: DisruptionPnrGroupRow;
  candidate: IRecoveryCandidate;
  why?: string | undefined;
}) {
  const { t } = useTranslation('pnr');
  const clock = useClock(group);
  const at = group.lastOfferResponse?.at;
  const alert = toneClasses('alert');
  return (
    <div className={cn('flex flex-col gap-1 rounded-lg border px-3 py-2.5', alert.surface, alert.border)} data-testid="recovery-reaccepted">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[15px]">
          <b>{candidate.flightNumber}</b> · {clock.dep(candidate.departAt)} → {clock.arr(candidate.arriveAt)}
          {at ? ` · ${t('recovery.acceptedAt', { time: clock.dep(at) })}` : ''}
        </span>
        <span className={cn('text-[13px] font-semibold', alert.text)}>{t('recovery.notBought')}</span>
      </div>
      {why ? <p className={cn('flex items-start gap-1.5 text-sm', alert.text)}><TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />{why}</p> : null}
    </div>
  );
}

/** The «Vuelo nuevo» service card's body while Nexa's purchase is refused / being recovered. */
export function RecoveryFlightBody({
  group,
  phase,
  view,
  loading,
  failed,
  onRetry,
  recovery,
  onChoose,
  refusalWhy,
}: {
  group: DisruptionPnrGroupRow;
  phase: PurchasePhase;
  view: IRecoveryView | undefined;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  recovery: RecoveryRun;
  onChoose: () => void;
  /** Why the purchase was refused, worded from the row (`Faltan datos de 2 pasajeros…`). */
  refusalWhy?: string | undefined;
}) {
  const { t } = useTranslation('pnr');
  const clock = useClock(group);
  if (loading && !view) return <ViewLoading />;
  if (failed && !view) return <ViewFailed onRetry={onRetry} />;
  if (!view) return null;
  const best = bestCandidate(view);
  const offered = view.reoffer ? view.candidates.find((c) => c.optionUrn === view.reoffer?.optionUrn) : undefined;
  const deciding = phase === 'NEEDS_OPERATOR' || phase === 'REVIEW';
  // They accepted the re-offer and THAT purchase failed: it is the flight in play, not «the best».
  const reaccepted = deciding ? reacceptedCandidate(view, group.lastOfferResponse) : undefined;

  return (
    <div className="flex flex-col gap-2.5" data-testid="recovery-flight">
      {view.failedAttempt || deciding ? <AcceptedGone group={group} view={view} answered={!reaccepted} /> : null}
      {reaccepted ? (
        <ReacceptedRow group={group} candidate={reaccepted} why={refusalWhy} />
      ) : phase === 'REOFFERED' && offered ? (
        <CandidateRow
          group={group}
          candidate={offered}
          badge={
            view.reoffer?.acceptBy
              ? t('recovery.offeredUntil', { time: clock.dep(view.reoffer.acceptBy) })
              : t('recovery.offered')
          }
        />
      ) : best && phase !== 'CONFIRMING' ? (
        <CandidateRow
          group={group}
          candidate={best}
          badge={
            best.sameAsAccepted
              ? t('recovery.candidate.sameAsAccepted')
              : best.equivalent
                ? t('recovery.equivalentBadge')
                : t('recovery.bestBadge')
          }
        />
      ) : deciding && view.searchedAt ? (
        <p className="text-sm text-muted-foreground" data-testid="recovery-none">
          {t('recovery.noneFound', { time: clock.dep(view.searchedAt) })}
        </p>
      ) : null}
      {deciding ? (
        <div className="flex flex-wrap items-center gap-2">
          <CardAction onClick={() => recovery.run({ kind: 'search' })} disabled={recovery.pending} testId="recovery-search">
            {recovery.pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('band.action.searchAgain')}
          </CardAction>
          <BuyWithoutAsking candidate={reaccepted ?? best} group={group} recovery={recovery} />
          {view.candidates.length > 0 ? (
            <CardAction onClick={onChoose} testId="recovery-choose">
              {t('band.action.seeOptions', { count: view.candidates.length })}
            </CardAction>
          ) : null}
        </div>
      ) : null}
      <RecoveryError error={recovery.error} action={recovery.action} />
    </div>
  );
}

/** «Elegir reemplazo» (mockup PnrRechazada): the fresh search, offer one, or the other ways out. */
export function ReplacementChooser({
  group,
  view,
  loading,
  failed,
  onRetry,
  recovery,
  policyLink,
  refusalWhy,
}: {
  group: DisruptionPnrGroupRow;
  view: IRecoveryView | undefined;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  recovery: RecoveryRun;
  policyLink?: ReactNode;
  /** Why the purchase was refused, worded from the row. */
  refusalWhy?: string | undefined;
}) {
  const { t } = useTranslation('pnr');
  const rt = useRecoveryT();
  const clock = useClock(group);
  const candidates = view?.candidates ?? [];
  const reaccepted = reacceptedCandidate(view, group.lastOfferResponse);
  const best = bestCandidate(view);
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [showAll, setShowAll] = useState(false);
  const chosen = candidates.find((c) => c.optionUrn === picked && offerable(c)) ?? best;
  const visible = showAll ? candidates : candidates.slice(0, 3);
  const provider = providerName(group.nexaPurchase?.provider ?? group.selectedProvider);
  const failedAt = view?.failedAttempt?.at;
  const equivalents = candidates.filter((c) => c.equivalent).length;
  const noFlights = !!view && candidates.length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="recovery-chooser">
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:px-6">
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <div className="flex flex-col gap-3">
            {reaccepted ? (
              // The re-offer they accepted is what could not be bought now — not the first flight.
              <section
                className={cn('flex flex-col gap-1.5 rounded-xl border p-4', toneClasses('alert').surface, toneClasses('alert').border)}
                data-testid="recovery-chooser-reaccepted"
              >
                <SectionTitle className={toneClasses('alert').text}>{t('recovery.chooser.acceptedTitle')}</SectionTitle>
                <p className="text-base font-bold">
                  {reaccepted.flightNumber} · {clock.dep(reaccepted.departAt)} → {clock.arr(reaccepted.arriveAt)}
                </p>
                <p className={cn('flex items-start gap-1.5 text-sm font-medium', toneClasses('alert').text)}>
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>{refusalWhy ?? t('recovery.notBought')}</span>
                </p>
              </section>
            ) : view?.accepted ? (
              <section className={cn('flex flex-col gap-1.5 rounded-xl border p-4', toneClasses('alert').surface, toneClasses('alert').border)}>
                <SectionTitle className={toneClasses('alert').text}>{t('recovery.chooser.acceptedTitle')}</SectionTitle>
                <p className="text-base font-bold text-muted-foreground line-through">
                  {view.accepted.flightNumber} · {clock.dep(view.accepted.departAt)} → {clock.arr(view.accepted.arriveAt)} ·{' '}
                  {view.accepted.cabin.toLowerCase()}
                </p>
                <p className={cn('flex items-start gap-1.5 text-sm font-medium', toneClasses('alert').text)} data-testid="recovery-chooser-failure">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    {failureClause(failureReasonOf(view), provider, rt, view.failedAttempt?.field, true)}
                    {failedAt ? <span className="font-normal text-muted-foreground"> · {clock.dep(failedAt)}</span> : null}
                  </span>
                </p>
              </section>
            ) : null}
            <section className="flex flex-col gap-1.5 rounded-xl border border-border bg-card p-4">
              <SectionTitle>{t('recovery.chooser.ruleTitle')}</SectionTitle>
              <p className="text-sm">{t('recovery.chooser.rule')}</p>
              {policyLink}
            </section>
          </div>

          <section className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4" aria-label={t('recovery.chooser.searchTitle')}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-bold">
                  {view?.searchedAt
                    ? t('recovery.chooser.searchAt', { time: clock.dep(view.searchedAt) })
                    : t('recovery.chooser.searchTitle')}
                </h3>
                {candidates.length > 0 ? (
                  <p className="text-[13px] text-muted-foreground" data-testid="recovery-search-summary">
                    {equivalents > 0
                      ? t('recovery.chooser.summaryEquivalent', { count: candidates.length, equivalents })
                      : t('recovery.chooser.summaryNoEquivalent', { count: candidates.length })}
                  </p>
                ) : null}
              </div>
              {/* With no flights, «Buscar de nuevo» is the empty state's own button. */}
              {noFlights ? null : (
                <CardAction onClick={() => recovery.run({ kind: 'search' })} disabled={recovery.pending} testId="recovery-search-again">
                  {recovery.pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                  {t('band.action.searchAgain')}
                </CardAction>
              )}
            </div>
            {loading && !view ? <ViewLoading /> : null}
            {failed && !view ? <ViewFailed onRetry={onRetry} /> : null}
            {noFlights ? (
              <div className="flex flex-col items-center gap-3 px-4 py-8 text-center" data-testid="recovery-chooser-empty">
                <span aria-hidden className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <SearchX className="h-6 w-6" />
                </span>
                <p className="text-base font-semibold">{t('recovery.chooser.empty')}</p>
                <Button
                  type="button"
                  className="h-11 px-5 text-[15px] font-semibold"
                  onClick={() => recovery.run({ kind: 'search' })}
                  disabled={recovery.pending}
                  data-testid="recovery-search-again"
                >
                  {recovery.pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <RotateCw className="h-4 w-4" aria-hidden />}
                  {t('band.action.searchAgain')}
                </Button>
              </div>
            ) : null}
            {candidates.length > 0 ? (
              <fieldset className="flex flex-col gap-1.5" data-testid="recovery-candidates">
                <legend className="sr-only">{t('recovery.chooser.legend')}</legend>
                {visible.map((c) => {
                  const selected = chosen?.optionUrn === c.optionUrn;
                  // Too soon, over the cap or without room: shown with why, never offered or bought.
                  const full = !offerable(c);
                  return (
                    <label
                      key={c.optionUrn}
                      className={cn(
                        'grid grid-cols-[1.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-0.5 rounded-lg px-3 py-2.5 sm:grid-cols-[1.5rem_6.5rem_9rem_minmax(0,1fr)_auto]',
                        full ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                        selected ? toneClasses('info').surface : !full && 'hover:bg-muted/40',
                      )}
                      data-testid="recovery-candidate"
                    >
                      <input
                        type="radio"
                        name={`recovery-${group.subCaseUrn}`}
                        className="h-4 w-4 accent-primary"
                        checked={selected}
                        disabled={full}
                        onChange={() => setPicked(c.optionUrn)}
                      />
                      <span className="text-[15px] font-bold">{c.flightNumber}</span>
                      <span className="col-start-2 text-[15px] tabular-nums sm:col-start-auto">
                        {clock.dep(c.departAt)} → {clock.arr(c.arriveAt)}
                      </span>
                      <span className={cn('col-start-2 text-sm sm:col-start-auto', VERDICT_CLASS[candidateVerdict(c)])}>
                        {candidateLine(c, rt)}
                      </span>
                      <span className="col-start-2 text-[13px] text-muted-foreground sm:col-start-auto">
                        {price(c) ? t('recovery.pricePerPax', { price: price(c) }) : null}
                      </span>
                    </label>
                  );
                })}
                {candidates.length > 3 && !showAll ? (
                  <button
                    type="button"
                    className="self-start px-3 text-[13px] text-primary hover:underline"
                    onClick={() => setShowAll(true)}
                    data-testid="recovery-show-all"
                  >
                    {t('recovery.chooser.more', { count: candidates.length - 3 })}
                  </button>
                ) : null}
              </fieldset>
            ) : null}
            {chosen ? (
              <p className="flex flex-wrap justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                <span>{t('recovery.chooser.note')}</span>
                <span className="text-muted-foreground">{t('recovery.chooser.noteBuy')}</span>
              </p>
            ) : null}
            <RecoveryError error={recovery.error} action={recovery.action} />
          </section>
        </div>
      </div>
      <footer
        className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-muted/30 px-4 py-3 sm:px-6"
        data-testid="recovery-chooser-footer"
      >
        <NoFlight group={group} recovery={recovery} />
        {/* Nothing to buy or offer without flights: those two only once there are options. */}
        {candidates.length > 0 ? (
          <>
            <BuyWithoutAsking candidate={chosen} group={group} recovery={recovery} trigger="footer" />
            <Button
              type="button"
              className="h-11 px-5 text-[15px] font-bold"
              disabled={!chosen || recovery.pending}
              onClick={() => chosen && recovery.run({ kind: 'offer', optionUrn: chosen.optionUrn })}
              data-testid="recovery-offer"
            >
              {recovery.pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {chosen?.sameAsAccepted
                ? t('recovery.chooser.buyAccepted')
                : chosen
                  ? t('recovery.chooser.offer', { flight: chosen.flightNumber })
                  : t('recovery.chooser.offerNone')}
            </Button>
          </>
        ) : null}
      </footer>
    </div>
  );
}

const CHECK_TONE: Record<string, string> = {
  NOT_FOUND: 'text-muted-foreground',
  FOUND: toneClasses('ok').text,
  ERROR: toneClasses('warn').text,
};

/** «Compra sin confirmar» (mockup PnrSinConfirmar): verify, or give up with an attestation. */
export function UnconfirmedPanel({
  group,
  view,
  loading,
  failed,
  onRetry,
  recovery,
}: {
  group: DisruptionPnrGroupRow;
  view: IRecoveryView | undefined;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  recovery: RecoveryRun;
}) {
  const { t } = useTranslation('pnr');
  const [attested, setAttested] = useState(false);
  const clock = useClock(group);
  const provider = providerName(group.nexaPurchase?.provider ?? group.selectedProvider);
  const u = view?.unconfirmed;
  const flight = view?.accepted?.flightNumber ?? group.assignedFlight?.number ?? '—';

  return (
    <div className="flex flex-col gap-3" data-testid="recovery-unconfirmed">
      <section className={cn('flex flex-col gap-2.5 rounded-xl border bg-card p-4', toneClasses('warn').border)}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-base font-bold">{t('recovery.unconfirmed.title')}</h3>
          {u?.attemptRef ? (
            <span className="font-mono text-[13px] text-muted-foreground">
              {t('recovery.unconfirmed.reference', { ref: u.attemptRef })}
            </span>
          ) : null}
        </div>
        {loading && !view ? <ViewLoading /> : null}
        {failed && !view ? <ViewFailed onRetry={onRetry} /> : null}
        {view ? (
          <ol className="grid grid-cols-[3.5rem_minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 text-sm" data-testid="recovery-checks">
            {view.failedAttempt ? (
              <li className="contents">
                <span className="tabular-nums text-muted-foreground">{clock.dep(view.failedAttempt.at)}</span>
                <span>{t('recovery.unconfirmed.attempt', { flight, provider })}</span>
                <span className={toneClasses('warn').text}>{t('recovery.unconfirmed.noAnswer')}</span>
              </li>
            ) : null}
            {(u?.checks ?? []).map((c) => (
              <li key={c.at} className="contents" data-testid="recovery-check">
                <span className="tabular-nums text-muted-foreground">{clock.dep(c.at)}</span>
                <span>{t('recovery.unconfirmed.check')}</span>
                <span className={CHECK_TONE[c.result]}>{t(`recovery.unconfirmed.result.${c.result}`)}</span>
              </li>
            ))}
            {u?.nextCheckAt ? (
              <li className="contents text-muted-foreground">
                <span className="tabular-nums">{clock.dep(u.nextCheckAt)}</span>
                <span>{t('recovery.unconfirmed.next')}</span>
                <span />
              </li>
            ) : null}
          </ol>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <CardAction onClick={() => recovery.run({ kind: 'reconcile' })} disabled={recovery.pending} testId="recovery-verify">
            {recovery.pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('band.action.verifyNow')}
          </CardAction>
        </div>
      </section>

      <section
        className={cn('flex flex-col gap-2 rounded-xl border p-4', toneClasses('alert').surface, toneClasses('alert').border)}
        data-testid="recovery-give-up"
      >
        <h3 className={cn('text-base font-bold', toneClasses('alert').text)}>{t('recovery.giveUp.title')}</h3>
        <p className="text-sm">{t('recovery.giveUp.body', { provider })}</p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-destructive"
            checked={attested}
            onChange={(e) => setAttested(e.target.checked)}
            data-testid="recovery-give-up-attest"
          />
          {t('recovery.giveUp.attest', { provider })}
        </label>
        <div>
          <RoleGate min={Role.OPS_SUPERVISOR} tooltip={t('recovery.supervisorOnly')}>
            <CardAction
              tone="danger"
              onClick={() => recovery.run({ kind: 'giveUp' })}
              disabled={!attested || recovery.pending}
              testId="recovery-give-up-confirm"
            >
              {t('recovery.giveUp.confirm')}
            </CardAction>
          </RoleGate>
        </div>
      </section>
      <RecoveryError error={recovery.error} action={recovery.action} />
      {group.governingPolicyUrn ? (
        <Link
          to={`/policies/${encodeURIComponent(group.governingPolicyUrn)}`}
          className="text-[13px] text-primary hover:underline"
        >
          {t('recovery.policyLink')}
        </Link>
      ) : null}
    </div>
  );
}
